import React, { useState, useRef, useEffect } from "react";
import { cleaningStateFillOpacity, cleaningStateStyle } from "./cleaningState";
import { formatArea } from "./locationArea";
import { computeFloorplanHandle } from "./handles";
import { buildCanvasRouteSegments } from "./routeGeometry";
import { gsap } from 'gsap';

const LocationCanvas = ({
  svgRef,
  rects,
  selectedId,
  editMode,
  drawMode,
  floorplanEditMode,
  floorplanEditLocation,
  handleMouseDown,
  draftRef,
  onToggleCrop,
  floorplanCropMode,
  cropSelection,
  onConfirmCrop,
  onSaveAndExit,
  onDeleteFloorplan,
  onCanvasDragStart,
  onCanvasDrag,
  onCanvasDragEnd,
  onRootLocationClick,
  rootSelectionOnly = false,
  canvasOffset = { x: 0, y: 0 },
  canvasZoom = 1.0,
  editLoctagMode = false,
  addMode = false,
  loctags = [],
  selectedLoctagId = null,
  hoveredLoctagId = null,
  setHoveredLoctagId,
  onLoctagClick,
  onLoctagDrag,
  onLoctagDragEnd,
  onLoctagDelete,
  cleanerPositions = [],
  locationSettingsMode = false,
  locationHoverFields = {},
  locationAreaById = new Map(),
  cleanRouteMode = false,
  routeSelectionEnabled = false,
  routeStepMap = {},
  checkedRoutes = [],
  otherRouteLocationIds = [],
  onRouteLocationClick,
  cleaningGroupLocationIds = [],
  onCleaningGroupLocationClick,
  onRouteStepClick,
  routeStepClickable = false,
  liveRoutePlans = [],
  technicalLocations = [],
  technicalCleaningStates = [],
  cleaningNow: cleaningNowProp = null,
  highlightedTechnicalLocationId = null,
}) => {
  const [hoveredId, setHoveredId] = useState(null);
  const [hoveredEdge, setHoveredEdge] = useState(null);
  const [hoveredDeviceId, setHoveredDeviceId] = useState(null);
  const cleanerRefs = useRef(new Map());
  const cleanerAnimations = useRef(new Map());
  const [cursor, setCursor] = useState("default");

  // Device icon animation – moves to last loctag position, no wandering
  useEffect(() => {
    const currentDeviceIds = cleanerPositions.map(p => p.deviceId);
    cleanerRefs.current.forEach((_, deviceId) => {
      if (!currentDeviceIds.includes(deviceId)) {
        const anim = cleanerAnimations.current.get(deviceId);
        if (anim && anim.kill) anim.kill();
        cleanerRefs.current.delete(deviceId);
        cleanerAnimations.current.delete(deviceId);
      }
    });

    cleanerPositions.forEach((pos) => {
      const element = cleanerRefs.current.get(pos.deviceId);
      if (!element) return;
      const currentAnim = cleanerAnimations.current.get(pos.deviceId);
      if (currentAnim && currentAnim.targetX === pos.x && currentAnim.targetY === pos.y) return;

      if (currentAnim && currentAnim.kill) currentAnim.kill();

      if (pos.instant) {
        gsap.set(element, { x: pos.x, y: pos.y });
        cleanerAnimations.current.set(pos.deviceId, { targetX: pos.x, targetY: pos.y, kill: () => {} });
        return;
      }

      const anim = gsap.to(element, {
        x: pos.x,
        y: pos.y,
        duration: 3,
        ease: "none",
        onComplete: function() {
          cleanerAnimations.current.set(pos.deviceId, { targetX: pos.x, targetY: pos.y, kill: () => anim.kill() });
        }
      });
      cleanerAnimations.current.set(pos.deviceId, { targetX: pos.x, targetY: pos.y, kill: () => anim.kill() });
    });
  }, [cleanerPositions]);
  
  // Loctag drag state
  const [draggingLoctag, setDraggingLoctag] = useState(null);
  const [dragStartPos, setDragStartPos] = useState(null);
  const showLocationBorders = locationHoverFields.show_borders !== false;

  useEffect(() => {
    if (!rootSelectionOnly) return;
    setHoveredId(null);
    setHoveredEdge(null);
    setHoveredLoctagId && setHoveredLoctagId(null);
    setCursor("default");
  }, [rootSelectionOnly, setHoveredLoctagId]);

  const hasValue = (value) => value !== undefined && value !== null && value !== "";

  const loctagIdByTechnicalLocationId = new Map(
    loctags
      .filter((tag) => tag.technical_location_id !== undefined && tag.technical_location_id !== null)
      .map((tag) => [String(tag.technical_location_id), tag.id])
  );

  const getTechnicalAreaLoctagId = (area) => (
    area.loctag_id
    ?? loctagIdByTechnicalLocationId.get(String(area.id))
    ?? null
  );

  // Takarítási állapot technikai locationönként: a szín a legutóbbi takarítás és a
  // következő cleaning group határidő közötti időből számolódik.
  // Visszajátszásnál a megjelenített múltbeli pillanat, élőben a jelen.
  const cleaningNow = cleaningNowProp ?? Date.now();
  const cleaningStyleByTechnicalLocationId = new Map(
    technicalCleaningStates
      .map((state) => [
        String(state.technical_location_id),
        cleaningStateStyle(state.cleaned_at, state.deadline_at, cleaningNow),
      ])
      .filter(([, style]) => style)
  );

  const getHoverRows = (location) => {
    const rows = [];

    if (locationHoverFields.name && hasValue(location.name)) rows.push(["name", location.name]);
    if (locationHoverFields.type && hasValue(location.type)) rows.push(["type", location.type]);
    if (locationHoverFields.id && hasValue(location.id)) rows.push(["id", location.id]);
    if (locationHoverFields.area_m2 && locationAreaById.has(String(location.id))) {
      rows.push(["area", formatArea(locationAreaById.get(String(location.id)))]);
    }
    if (locationHoverFields.parent_location_id && hasValue(location.parent_location_id)) {
      rows.push(["parent", location.parent_location_id]);
    }
    if (locationHoverFields.loctag_count && hasValue(location.loctag_count)) {
      rows.push(["loctags", location.loctag_count]);
    }
    if (locationHoverFields.pixels_per_meter && hasValue(location.pixels_per_meter)) {
      rows.push(["ppm(root)", location.pixels_per_meter]);
    }
    if (
      locationHoverFields.effective_pixels_per_meter &&
      hasValue(location.effective_pixels_per_meter)
    ) {
      rows.push(["ppm(eff)", location.effective_pixels_per_meter]);
    }
    if (
      locationHoverFields.scale_source_location_id &&
      hasValue(location.scale_source_location_id)
    ) {
      rows.push(["scale src", location.scale_source_location_id]);
    }
    if (locationHoverFields.x && hasValue(location.x)) rows.push(["x", location.x]);
    if (locationHoverFields.y && hasValue(location.y)) rows.push(["y", location.y]);
    if (locationHoverFields.width && hasValue(location.width)) rows.push(["width", location.width]);
    if (locationHoverFields.height && hasValue(location.height)) rows.push(["height", location.height]);
    if (locationHoverFields.floorplan_id && hasValue(location.floorplan_id)) {
      rows.push(["floorplan", location.floorplan_id]);
    }

    return rows;
  };

  const getInlineRows = (location) => {
    const rows = getHoverRows(location).map(([label, value]) => `${label}: ${String(value)}`);
    const maxRows = Math.max(0, Math.floor((location.height - 6) / 12));
    if (maxRows <= 0) return [];
    return rows.slice(0, maxRows);
  };
  
  // Cursor reset amikor addMode vagy drawMode kikapcsol
  React.useEffect(() => {
    if (!addMode && !drawMode && !floorplanEditMode) {
      setCursor("default");
    }
  }, [addMode, drawMode, floorplanEditMode]);
  
  // Touch state - tap vs long press/drag megkülönböztetéshez
  const touchStartTimeRef = React.useRef(null);
  const touchStartPosRef = React.useRef(null);

  // Él-érzékelési sáv képernyőpixelben: zoomtól függetlenül ennyi px-en belül vált a kurzor
  const EDGE_HIT_PX = 8;
  const getEdgeMargin = (r) => {
    const ctm = svgRef.current?.getScreenCTM();
    const screenScale = (ctm ? Math.hypot(ctm.a, ctm.b) : 1) * canvasZoom;
    const margin = EDGE_HIT_PX / (screenScale || 1);
    // Kis location-nél a közepe maradjon mozgatható
    return Math.min(margin, r.width / 3, r.height / 3);
  };

  // A sáv az élen belül és kívül is aktív
  const getEdgeType = (r, x, y, margin = getEdgeMargin(r)) => {
    const withinY = y >= r.y - margin && y <= r.y + r.height + margin;
    const withinX = x >= r.x - margin && x <= r.x + r.width + margin;
    const left = withinY && Math.abs(x - r.x) <= margin;
    const right = withinY && Math.abs(x - (r.x + r.width)) <= margin;
    const top = withinX && Math.abs(y - r.y) <= margin;
    const bottom = withinX && Math.abs(y - (r.y + r.height)) <= margin;
    const insideX = x > r.x + margin && x < r.x + r.width - margin;
    const insideY = y > r.y + margin && y < r.y + r.height - margin;
    if ((left && top) || (right && bottom) || (left && bottom) || (right && top)) {
      if ((left && top) || (right && bottom)) return "nwse-resize";
      if ((right && top) || (left && bottom)) return "nesw-resize";
    }
    if (left) return "ew-resize";
    if (right) return "ew-resize";
    if (top) return "ns-resize";
    if (bottom) return "ns-resize";
    if (insideX && insideY) return "move";
    return null;
  };

  // A kiválasztott location éle elsőbbséget kap a felette/mellette lévő child-okkal szemben
  const getSelectedEdgeAt = (x, y) => {
    if (!selectedId) return null;
    const selected = rects.find((r) => r.id === selectedId);
    if (!selected) return null;
    const edgeType = getEdgeType(selected, x, y);
    return edgeType && edgeType !== "move" ? edgeType : null;
  };

  const getTopmostLocationAt = (x, y) => {
    const hovered = rects
      .filter((r) => x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height)
      .sort((a, b) => b._depth - a._depth);
    const maxDepth = Math.max(...hovered.map((r) => r._depth), -1);
    return hovered.find((r) => r._depth === maxDepth) || null;
  };

  const routeMarkerRadius = 16;

  const ROUTE_PALETTE = ['#0F766E','#10B981','#F59E0B','#0e9488','#059669','#d97706','#134e4a','#065f46'];

  function computeAdaptiveRoute(locationRects, rawEntries) {
    const rectById = new Map(locationRects.map((r) => [String(r.id), r]));
    const entries = rawEntries.map((entry) => {
      const loc = rectById.get(String(entry.locationId));
      const cx = loc ? loc.x + loc.width / 2 : entry.centerX;
      const cy = loc ? loc.y + loc.height / 2 : entry.centerY;
      return { ...entry, wx: cx, wy: cy, centerX: cx, centerY: cy };
    });
    return { entries, segments: buildCanvasRouteSegments(entries) };
  }

  const checkedRoutesData = (checkedRoutes || []).map((route, idx) => {
    const color = ROUTE_PALETTE[idx % ROUTE_PALETTE.length];
    const rawEntries = (route.steps || [])
      .map((step) => {
        const location = rects.find((r) => String(r.id) === String(step.location_id));
        if (!location || location.is_root) return null;
        return { locationId: step.location_id, stepOrder: step.step_order, centerX: location.x, centerY: location.y };
      })
      .filter(Boolean)
      .sort((a, b) => a.stepOrder - b.stepOrder);
    const { entries, segments } = computeAdaptiveRoute(rects, rawEntries);
    return { route, color, entries, segments };
  });

  const rawRouteStepEntries = cleanRouteMode
    ? Object.entries(routeStepMap || {})
        .map(([locationId, stepOrder]) => {
          const location = rects.find((r) => String(r.id) === String(locationId));
          if (!location || location.is_root) return null;
          return { locationId, stepOrder: Number(stepOrder), centerX: location.x, centerY: location.y };
        })
        .filter((entry) => entry && Number.isFinite(entry.stepOrder))
        .sort((a, b) => a.stepOrder - b.stepOrder)
    : [];

  const { entries: routeStepEntries, segments: routeStepSegments } =
    computeAdaptiveRoute(rects, rawRouteStepEntries);

  const handleMouseMove = (e) => {
    if (!svgRef.current) return;

    if (rootSelectionOnly) {
      setHoveredId(null);
      setHoveredEdge(null);
      setHoveredLoctagId && setHoveredLoctagId(null);
      setCursor("default");
      if (onCanvasDrag) onCanvasDrag(e);
      return;
    }
    
    // AddMode (új loctag hozzáadás) - crosshair cursor
    if (editLoctagMode && addMode) {
      setCursor("crosshair");
      if (onCanvasDrag) onCanvasDrag(e);
      return;
    }

    // Loctag nézetben a teljes technikai location legyen a hover célterület.
    // A vászonkoordinátából számolunk, így az eredmény nem függ attól, melyik
    // SVG-réteg kapta meg közvetlenül a böngésző mouseenter eseményét.
    if (editLoctagMode && !addMode && !draggingLoctag) {
      const isOverLoctagMenu = e.target?.closest?.(".loctag-menu");

      if (!isOverLoctagMenu) {
        const directTagId = e.target?.closest?.("[data-loctag-id]")?.getAttribute?.("data-loctag-id");
        const pt = svgRef.current.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
        const offsetX = canvasOffset?.x || 0;
        const offsetY = canvasOffset?.y || 0;
        const x = (svgP.x - offsetX) / canvasZoom;
        const y = (svgP.y - offsetY) / canvasZoom;
        const hoveredTechnicalLocation = [...technicalLocations]
          .reverse()
          .find((area) => (
            x >= area.x
            && x <= area.x + area.width
            && y >= area.y
            && y <= area.y + area.height
          ));
        const hoveredTechnicalLoctagId = hoveredTechnicalLocation
          ? getTechnicalAreaLoctagId(hoveredTechnicalLocation)
          : null;
        const nextHoveredLoctagId = directTagId ?? hoveredTechnicalLoctagId;
        setHoveredLoctagId && setHoveredLoctagId(nextHoveredLoctagId);
      }
    }

    if (typeof onCleaningGroupLocationClick === "function") {
      const pt = svgRef.current.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
      const x = (svgP.x - (canvasOffset?.x || 0)) / canvasZoom;
      const y = (svgP.y - (canvasOffset?.y || 0)) / canvasZoom;
      const topmost = getTopmostLocationAt(x, y);
      setCursor(topmost && !topmost.is_root ? "pointer" : "default");
      if (onCanvasDrag) onCanvasDrag(e);
      return;
    }

    if (cleanRouteMode && typeof onRouteLocationClick === "function") {
      const pt = svgRef.current.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
      const offsetX = canvasOffset?.x || 0;
      const offsetY = canvasOffset?.y || 0;
      const x = (svgP.x - offsetX) / canvasZoom;
      const y = (svgP.y - offsetY) / canvasZoom;
      const topmost = getTopmostLocationAt(x, y);
      const isBlocked = topmost && (topmost.is_root || otherRouteLocationIds.includes(String(topmost.id)));
      setCursor(isBlocked ? "not-allowed" : topmost ? "pointer" : "default");
      if (onCanvasDrag) onCanvasDrag(e);
      return;
    }
    
    // Loctag drag kezelése (csak ideiglenes piros tag mozgatható!)
    if (draggingLoctag && draggingLoctag.isTemporary) {
      const pt = svgRef.current.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
      const offsetX = canvasOffset?.x || 0;
      const offsetY = canvasOffset?.y || 0;
      const x = (svgP.x - offsetX) / canvasZoom;
      const y = (svgP.y - offsetY) / canvasZoom;
      
      if (onLoctagDrag) {
        onLoctagDrag(draggingLoctag, x, y);
      }
      return;
    }
    
    // Canvas drag normál nézetben is
    if (onCanvasDrag) onCanvasDrag(e);
    
    // Ha nincs edit mód, ne jelenjenek meg hover effektek
    if (!editMode && !locationSettingsMode) {
      return;
    }
    
    // Floorplan edit mode kezelése
    if (floorplanEditMode && floorplanEditLocation) {
      // Pontos koordináta számítás SVG transform-el + canvas offset és zoom kompenzáció
      const pt = svgRef.current.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
      const offsetX = canvasOffset?.x || 0;
      const offsetY = canvasOffset?.y || 0;
      const x = (svgP.x - offsetX) / canvasZoom;
      const y = (svgP.y - offsetY) / canvasZoom;

      // Crop mód – kurzor
      if (floorplanCropMode) {
        setCursor("crosshair");
        if (onCanvasDrag) onCanvasDrag(e);
        return;
      }

      const handle = computeFloorplanHandle(floorplanEditLocation, x, y, { x: 0, y: 0 }, 6);
      setHoveredEdge(handle);
      setHoveredId(handle ? floorplanEditLocation.id : null);

      if (handle === "left" || handle === "right") setCursor("ew-resize");
      else if (handle === "top" || handle === "bottom") setCursor("ns-resize");
      else if (handle === "top-left" || handle === "bottom-right") setCursor("nwse-resize");
      else if (handle === "top-right" || handle === "bottom-left") setCursor("nesw-resize");
      else if (handle === "inside") setCursor("move");
      else setCursor("default");

      if (onCanvasDrag) onCanvasDrag(e);
      return;
    }

    // Draw mode-ban ne jelenjenek meg hover effektek
    if (drawMode) {
      setHoveredId(null);
      setHoveredEdge(null);
      setCursor("crosshair");
      if (onCanvasDrag) onCanvasDrag(e);
      return;
    }

    const pt = svgRef.current.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
    const offsetX = canvasOffset?.x || 0;
    const offsetY = canvasOffset?.y || 0;
    const x = (svgP.x - offsetX) / canvasZoom;
    const y = (svgP.y - offsetY) / canvasZoom;
    let hovered = rects
      .filter((r) => x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height)
      .sort((a, b) => b._depth - a._depth);
    const maxDepth = Math.max(...hovered.map(r => r._depth), -1);
    const topmost = hovered.find(r => r._depth === maxDepth);

    const selectedEdge = editMode ? getSelectedEdgeAt(x, y) : null;

    // Normál edit mode kezelése
    if (selectedEdge) {
      setHoveredId(selectedId);
      setHoveredEdge(selectedEdge);
      setCursor(selectedEdge);
    } else if (editMode && selectedId && topmost && topmost.id === selectedId) {
      setHoveredId(selectedId);
      const edgeType = getEdgeType(topmost, x, y);
      setHoveredEdge(edgeType);
      setCursor(edgeType ? (edgeType === "move" ? "move" : edgeType) : "default");
    } else if (editMode && !selectedId && topmost) {
      setHoveredId(topmost.id);
      setHoveredEdge(null);
      setCursor("pointer");
    } else if (locationSettingsMode && topmost) {
      setHoveredId(topmost.id);
      setHoveredEdge(null);
      if (!editMode) setCursor("default");
    } else {
      setHoveredId(null);
      setHoveredEdge(null);
      setCursor("default");
    }
    if (onCanvasDrag) onCanvasDrag(e);
  };

  const handleClick = (e) => {
    if (!svgRef.current) return;

    if (rootSelectionOnly) {
      if ((e.type === "click" || e.type === "touchend") && typeof onRootLocationClick === "function") {
        const pointer = e.changedTouches?.[0] || e;
        const pt = svgRef.current.createSVGPoint();
        pt.x = pointer.clientX;
        pt.y = pointer.clientY;
        const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
        const offsetX = canvasOffset?.x || 0;
        const offsetY = canvasOffset?.y || 0;
        const x = (svgP.x - offsetX) / canvasZoom;
        const y = (svgP.y - offsetY) / canvasZoom;
        const clickedRoot = [...rects]
          .reverse()
          .find((r) => (
            r.is_root
            && x >= r.x
            && x <= r.x + r.width
            && y >= r.y
            && y <= r.y + r.height
          ));
        if (clickedRoot) onRootLocationClick(clickedRoot.id);
        return;
      }
      if (!editMode && typeof handleMouseDown === "function") handleMouseDown(e);
      return;
    }

    // Cleaning group szerkesztése: a kattintott (legmélyebb) location be/ki.
    // Csak a befejezett kattintás vált; a lenyomás (az SVG onMouseDown-ja is ide
    // fut) nem, különben egy kattintás be- és rögtön ki is venné a locationt.
    if (typeof onCleaningGroupLocationClick === "function") {
      if (e.type !== "click" && e.type !== "touchend") {
        if (!editMode && typeof handleMouseDown === "function") handleMouseDown(e);
        return;
      }
      const pointer = e.changedTouches?.[0] || e;
      const pt = svgRef.current.createSVGPoint();
      pt.x = pointer.clientX;
      pt.y = pointer.clientY;
      const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
      const x = (svgP.x - (canvasOffset?.x || 0)) / canvasZoom;
      const y = (svgP.y - (canvasOffset?.y || 0)) / canvasZoom;
      const topmost = getTopmostLocationAt(x, y);
      if (topmost && !topmost.is_root) onCleaningGroupLocationClick(topmost.id);
      return;
    }

    if (cleanRouteMode && typeof onRouteLocationClick === "function") {
      const pt = svgRef.current.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
      const offsetX = canvasOffset?.x || 0;
      const offsetY = canvasOffset?.y || 0;
      const x = (svgP.x - offsetX) / canvasZoom;
      const y = (svgP.y - offsetY) / canvasZoom;
      const topmost = getTopmostLocationAt(x, y);
      if (topmost && !topmost.is_root && !otherRouteLocationIds.includes(String(topmost.id))) {
        onRouteLocationClick(topmost.id);
      }
      return;
    }

    // Loctag mód prioritása - ha editLoctagMode ÉS addMode aktív, akkor a loctag kezelő fut
    // Ha csak editLoctagMode van (nézés mód), akkor engedjük a canvas drag-et
    if (editLoctagMode && addMode && onLoctagClick) {
      onLoctagClick(e);
      return;
    }
    
    // Ha nincs edit mód (és nincs addMode)
    if (!editMode && !addMode) {
      // locationSettingsMode-ban klikkelésre location kiválasztása (nem canvas drag)
      if (locationSettingsMode) {
        const pt = svgRef.current.createSVGPoint();
        pt.x = e.clientX;
        pt.y = e.clientY;
        const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
        const offsetX = canvasOffset?.x || 0;
        const offsetY = canvasOffset?.y || 0;
        const x = (svgP.x - offsetX) / canvasZoom;
        const y = (svgP.y - offsetY) / canvasZoom;
        const hovered = rects
          .filter((r) => x >= r.x && x <= r.x + r.width && y >= r.y && y <= r.y + r.height)
          .sort((a, b) => b._depth - a._depth);
        const maxDepth = Math.max(...hovered.map(r => r._depth), -1);
        const topmost = hovered.find(r => r._depth === maxDepth);
        if (topmost && typeof handleMouseDown === "function") {
          handleMouseDown({ ...e, hoveredId: topmost.id, selectOnly: true });
        }
        return;
      }
      if (typeof handleMouseDown === "function") {
        handleMouseDown(e);
      }
      return;
    }
    
    // Floorplan edit mode-ban közvetlenül a handleMouseDown-t hívjuk
    if (floorplanEditMode) {
      if (typeof handleMouseDown === "function") {
        handleMouseDown(e);
      }
      return;
    }

    // Draw mode-ban ne jelenjenek meg click effektek
    if (drawMode) {
      if (typeof handleMouseDown === "function") {
        handleMouseDown(e);
      }
      return;
    }

    // Pontos koordináta számítás SVG transform-el + canvas offset és zoom kompenzáció
    const pt = svgRef.current.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
    const offsetX = canvasOffset?.x || 0;
    const offsetY = canvasOffset?.y || 0;
    const x = (svgP.x - offsetX) / canvasZoom;
    const y = (svgP.y - offsetY) / canvasZoom;
    
    // A kiválasztott location élének megfogása (belül vagy kívül) elsőbbséget kap
    const selectedEdge = getSelectedEdgeAt(x, y);
    if (selectedEdge) {
      if (typeof handleMouseDown === "function") {
        handleMouseDown({ ...e, hoveredId: selectedId, hoveredEdge: selectedEdge });
      }
      return;
    }

    // Location edit módban: ellenőrizzük, hogy location-ön klikkelünk-e
    const topmost = getTopmostLocationAt(x, y);
    
    // Ha van location alatt a klikk, akkor location szerkesztés
    if (topmost) {
      if (selectedId === topmost.id) {
        // Már ki van választva - edge/resize kezelés
        const edgeType = getEdgeType(topmost, x, y);
        if (typeof handleMouseDown === "function") {
          handleMouseDown({ ...e, hoveredId: topmost.id, hoveredEdge: edgeType });
        }
      } else {
        // Új location kiválasztása
        if (typeof handleMouseDown === "function") {
          handleMouseDown({ ...e, hoveredId: topmost.id });
        }
      }
    } else {
      // Üres területre klikkelés edit módban → canvas drag vagy deselect
      if (typeof handleMouseDown === "function") {
        handleMouseDown({ ...e, hoveredId: null, hoveredEdge: null });
      }
    }
  };

  return (
    <svg
      ref={svgRef}
      className="w-full h-full bg-white"
      onMouseDown={handleClick}
      onClick={rootSelectionOnly ? handleClick : undefined}
      onMouseMove={handleMouseMove}
      onMouseUp={onCanvasDragEnd}
      onMouseLeave={() => {
        setHoveredId(null);
        setHoveredEdge(null);
        if (editLoctagMode) setHoveredLoctagId && setHoveredLoctagId(null);
      }}
      style={{ cursor, touchAction: 'none' }}
    >
      {/* SVG pattern defs */}
      <defs>
        <pattern id="other-route-hatch" patternUnits="userSpaceOnUse" width="10" height="10" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="10" stroke="rgba(100,116,139,0.55)" strokeWidth="4"/>
        </pattern>
        <pattern id="technical-location-hover-hatch" patternUnits="userSpaceOnUse" width="9" height="9" patternTransform="rotate(45)">
          <rect width="9" height="9" fill="rgba(239,68,68,0.1)"/>
          <line x1="0" y1="0" x2="0" y2="9" stroke="rgba(239,68,68,0.72)" strokeWidth="3"/>
        </pattern>
        <filter id="loctag-hover-glow" x="-180%" y="-180%" width="460%" height="460%">
          <feGaussianBlur stdDeviation="4" result="wideBlur"/>
          <feGaussianBlur in="SourceGraphic" stdDeviation="1.4" result="tightBlur"/>
          <feMerge>
            <feMergeNode in="wideBlur"/>
            <feMergeNode in="wideBlur"/>
            <feMergeNode in="tightBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <style>{`
          @keyframes loctag-hover-pulse {
            0%, 100% { opacity: 0.92; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.35); }
          }
          .loctag-hover-halo {
            animation: loctag-hover-pulse 0.8s ease-in-out infinite;
            transform-box: fill-box;
            transform-origin: center;
          }
        `}</style>
      </defs>

      {/* Locations group with zoom and offset transform */}
      <g transform={`translate(${canvasOffset?.x || 0}, ${canvasOffset?.y || 0}) scale(${canvasZoom})`}>
        {/* ClipPath defs for clipping floorplan images to location boundaries */}
        <defs>
          {rects.filter(r => r.floorplan_id).map(r => (
            <clipPath key={`clip-loc-${r.id}`} id={`clip-loc-${r.id}`}>
              <rect x={r.x} y={r.y} width={r.width} height={r.height} />
            </clipPath>
          ))}
        </defs>
        {rects.map((r) => (
        <g key={r.id || `${r.x}-${r.y}`}> 
          {r.floorplan_id && !(floorplanEditMode && floorplanEditLocation && floorplanEditLocation.id === r.id) && (
            <g clipPath={`url(#clip-loc-${r.id})`}>
              <image
                href={`/media/${r.floorplan_stored_name || r.floorplan_id + '.jpg'}`}
                x={r.x + (r.floorplan_offset_x || 0)}
                y={r.y + (r.floorplan_offset_y || 0)}
                width={r.floorplan_width || r.width}
                height={r.floorplan_height || r.height}
                preserveAspectRatio="none"
                style={{ 
                  pointerEvents: "none",
                  clipPath: (r.floorplan_crop_left !== undefined && r.floorplan_crop_top !== undefined && r.floorplan_crop_right !== undefined && r.floorplan_crop_bottom !== undefined) ? 
                    `inset(${r.floorplan_crop_top}px ${(r.floorplan_width || r.width) - r.floorplan_crop_right}px ${(r.floorplan_height || r.height) - r.floorplan_crop_bottom}px ${r.floorplan_crop_left}px)` : 
                    'none',
                  opacity: 1
                }}
              />
            </g>
          )}
          <rect
            data-location-id={r.id}
            x={r.x}
            y={r.y}
            width={r.width}
            height={r.height}
            fill={"none"}
            stroke={
              locationSettingsMode && String(hoveredId) === String(r.id)
                ? "#ef4444"
                : (editMode || showLocationBorders)
                ? (selectedId === r.id ? "#ef4444" : "#555")
                : "transparent"
            }
            strokeWidth={
              locationSettingsMode && String(hoveredId) === String(r.id)
                ? 3
                : selectedId === r.id
                ? 2
                : (editMode && !selectedId && hoveredId === r.id ? 3 : 1)
            }
            vectorEffect="non-scaling-stroke"
            style={{ 
              cursor: drawMode ? 'crosshair' : editMode ? 'pointer' : 'default', 
              pointerEvents: drawMode ? 'none' : 'all',
              touchAction: 'none'
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleClick(e);
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              e.preventDefault();
              // Mobilon is hívjuk meg a click handlert
              handleClick(e);
            }}
          />

          
          {/* Non-selected location display (checkbox-driven fields) */}
          {(!editMode || selectedId !== r.id) && (() => {
            const inlineRows = getInlineRows(r);
            if (inlineRows.length === 0) return null;

            return (
              <g style={{ pointerEvents: "none" }}>
                {inlineRows.map((row, idx) => (
                  <text
                    key={`${r.id}-row-${idx}`}
                    x={r.x + 3}
                    y={r.y + 11 + idx * 12}
                    fontSize="10"
                    fill="#111827"
                  >
                    {row}
                  </text>
                ))}
              </g>
            );
          })()}

          {/* Location settings hover panel */}
          {locationSettingsMode && hoveredId === r.id && (() => {
            const rows = getHoverRows(r);
            if (rows.length === 0) return null;

            const panelHeight = Math.max(24, rows.length * 16 + 8);
            const panelY = r.y - panelHeight - 6;

            return (
              <foreignObject
                x={r.x}
                y={panelY}
                width="220"
                height={panelHeight}
                style={{ pointerEvents: "none" }}
              >
                <div className="w-full h-full bg-slate-900/95 border border-slate-600 rounded px-2 py-1 text-[10px] text-slate-100 shadow">
                  {rows.map(([label, value]) => (
                    <div key={label} className="flex justify-between gap-2 leading-4">
                      <span className="text-slate-400">{label}</span>
                      <span className="font-mono text-right break-all">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </foreignObject>
            );
          })()}

          {/* Ha floorplan edit módban ez a location az aktív, akkor a floorplan-t a rect után (előrébb) rendereljük, félig áttetszően, és a UI gombokkal */}
          {r.floorplan_id && floorplanEditMode && floorplanEditLocation && floorplanEditLocation.id === r.id && (
            <g clipPath={`url(#clip-loc-${r.id})`}>
              <image
                href={`/media/${r.floorplan_stored_name || r.floorplan_id + '.jpg'}`}
                x={r.x + (r.floorplan_offset_x || 0)}
                y={r.y + (r.floorplan_offset_y || 0)}
                width={r.floorplan_width || r.width}
                height={r.floorplan_height || r.height}
                preserveAspectRatio="none"
                style={{ 
                  pointerEvents: "auto",
                  clipPath: (r.floorplan_crop_left !== undefined && r.floorplan_crop_top !== undefined && r.floorplan_crop_right !== undefined && r.floorplan_crop_bottom !== undefined) ? 
                    `inset(${r.floorplan_crop_top}px ${(r.floorplan_width || r.width) - r.floorplan_crop_right}px ${(r.floorplan_height || r.height) - r.floorplan_crop_bottom}px ${r.floorplan_crop_left}px)` : 
                    'none',
                  opacity: 0.85
                }}
              />
              {/* Szürke keret a floorplan edit módban */}
              <rect
                x={r.x + (r.floorplan_offset_x || 0)}
                y={r.y + (r.floorplan_offset_y || 0)}
                width={r.floorplan_width || r.width}
                height={r.floorplan_height || r.height}
                fill="none"
                stroke="#999"
                strokeWidth="1"
                style={{ pointerEvents: "none" }}
              />

              {/* Crop selection overlay */}
              {floorplanCropMode && cropSelection && (
                <g>
                  <rect
                    x={cropSelection.x}
                    y={cropSelection.y}
                    width={cropSelection.width}
                    height={cropSelection.height}
                    fill="rgba(0,0,0,0.05)"
                    stroke="#0aa"
                    strokeDasharray="4"
                  />
                  <foreignObject x={cropSelection.x + cropSelection.width - 70} y={cropSelection.y + cropSelection.height + 4} width="68" height="18" onMouseDown={(e)=>e.stopPropagation()}>
                    <button className="text-[10px] bg-white border border-gray-300 rounded px-2 w-full" onClick={(e)=>{e.stopPropagation(); onConfirmCrop && onConfirmCrop();}}>Crop it?</button>
                  </foreignObject>
                </g>
              )}
            </g>
          )}
          
        </g>
      ))}

        {technicalLocations.length > 0 && cleaningStyleByTechnicalLocationId.size > 0 && (
          <g style={{ pointerEvents: "none" }}>
            {technicalLocations.map((area) => {
              const style = !area.is_preview && cleaningStyleByTechnicalLocationId.get(String(area.id));
              if (!style) return null;
              return (
                <rect
                  key={`clean-state-${area.id}`}
                  data-cleaning-state={style.state}
                  data-technical-location-id={area.id}
                  x={area.x}
                  y={area.y}
                  width={area.width}
                  height={area.height}
                  fill={style.fill}
                  fillOpacity={cleaningStateFillOpacity(style)}
                  stroke="none"
                />
              );
            })}
          </g>
        )}

        {highlightedTechnicalLocationId !== null && technicalLocations.map((area) => (
          String(area.id) === String(highlightedTechnicalLocationId) ? (
            <rect
              key={`technical-list-highlight-${area.id}`}
              data-technical-list-highlight={area.id}
              x={area.x}
              y={area.y}
              width={area.width}
              height={area.height}
              fill="url(#technical-location-hover-hatch)"
              stroke="#ef4444"
              strokeWidth="3.5"
              vectorEffect="non-scaling-stroke"
              style={{ pointerEvents: "none" }}
            />
          ) : null
        ))}

        {editLoctagMode && technicalLocations.length > 0 && (
          <g>
            {technicalLocations.map((area) => {
              const areaLoctagId = getTechnicalAreaLoctagId(area);
              const isHovered = areaLoctagId !== null
                && String(hoveredLoctagId) === String(areaLoctagId);
              return (
              <g
                key={`${area.is_preview ? "preview" : "saved"}-${area.id}`}
                data-loctag-area-id={areaLoctagId}
              >
                <rect
                  x={area.x}
                  y={area.y}
                  width={area.width}
                  height={area.height}
                  fill={isHovered ? "url(#technical-location-hover-hatch)" : "transparent"}
                  stroke="#ef4444"
                  strokeOpacity={isHovered ? 1 : 0.72}
                  strokeWidth={isHovered ? 3.5 : 1.5}
                  strokeDasharray={area.is_preview ? "7 4" : "4 3"}
                  vectorEffect="non-scaling-stroke"
                  style={{ cursor: "pointer", pointerEvents: "all" }}
                />
              </g>
              );
            })}
          </g>
        )}

        {/* Route location coverage overlay — rects UTÁN, vonalak ELŐTT */}
        {cleanRouteMode && routeStepEntries.length > 0 && (() => {
          const maxStep = Math.max(...routeStepEntries.map((e) => e.stepOrder));
          return (
            <g style={{ pointerEvents: "none" }}>
              {routeStepEntries.map((entry) => {
                const loc = rects.find((r) => String(r.id) === String(entry.locationId));
                if (!loc || loc.is_root) return null;
                const isLast = entry.stepOrder === maxStep;
                return (
                  <rect
                    key={`route-cov-${entry.locationId}`}
                    x={loc.x}
                    y={loc.y}
                    width={loc.width}
                    height={loc.height}
                    fill={isLast ? "rgba(71,85,105,0.55)" : "rgba(71,85,105,0.28)"}
                  />
                );
              })}
            </g>
          );
        })()}

        {/* Szerkesztett cleaning group locationjei — tiszta türkiz kiemelés */}
        {cleaningGroupLocationIds.length > 0 && (
          <g style={{ pointerEvents: "none" }}>
            {cleaningGroupLocationIds.map((locationId) => {
              const loc = rects.find((r) => String(r.id) === String(locationId));
              if (!loc || loc.is_root) return null;
              return (
                <rect
                  key={`cleaning-group-${locationId}`}
                  data-cleaning-group-location-id={locationId}
                  x={loc.x}
                  y={loc.y}
                  width={loc.width}
                  height={loc.height}
                  fill="rgba(45, 212, 191, 0.45)"
                  stroke="#14b8a6"
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                />
              );
            })}
          </g>
        )}

        {/* Other-route hatching overlay — covers locations already claimed by other routes */}
        {cleanRouteMode && otherRouteLocationIds.length > 0 && (
          <g style={{ pointerEvents: "none" }}>
            {otherRouteLocationIds.map((locId) => {
              const loc = rects.find((r) => String(r.id) === locId);
              if (!loc || loc.is_root) return null;
              return (
                <rect
                  key={`other-route-${locId}`}
                  x={loc.x}
                  y={loc.y}
                  width={loc.width}
                  height={loc.height}
                  fill="url(#other-route-hatch)"
                />
              );
            })}
          </g>
        )}

        {/* Szerkesztett route vonalak - rects UTÁN, hogy ne fedődjön le az alaprajzok által */}
        {cleanRouteMode && routeStepSegments.length > 0 && (
          <g style={{ pointerEvents: "none" }}>
            {routeStepSegments.map((segment) => (
              <polyline
                key={`route-segment-${segment.key}`}
                points={segment.points.map(([x, y]) => `${x},${y}`).join(' ')}
                stroke="#9ca3af"
                strokeWidth={8}
                strokeLinejoin="miter"
                strokeLinecap="square"
                fill="none"
                opacity={0.95}
              />
            ))}
          </g>
        )}
        {/* Szerkesztett route körök - vonalak UTÁN, hogy felül legyenek */}
        {cleanRouteMode && routeStepEntries.map((entry) => (
          <g
            key={`edit-step-${entry.locationId}`}
            style={{ pointerEvents: routeStepClickable ? "auto" : "none", cursor: routeStepClickable ? "pointer" : "default" }}
            onClick={(e) => {
              if (!routeStepClickable || !onRouteStepClick) return;
              e.stopPropagation();
              onRouteStepClick(entry.locationId);
            }}
          >
            <circle
              cx={entry.centerX}
              cy={entry.centerY}
              r={routeMarkerRadius}
              fill={routeStepClickable ? "#0d9488" : "#9ca3af"}
              stroke="#4b5563"
              strokeWidth={2}
            />
            <text
              x={entry.centerX}
              y={entry.centerY + 5}
              fontSize={routeStepClickable ? "16" : "14"}
              textAnchor="middle"
              fill="#f9fafb"
              fontWeight="bold"
              style={{ pointerEvents: "none" }}
            >
              {entry.stepOrder}
            </text>
          </g>
        ))}

        {/* Bepipált route-ok - location overlay */}
        {checkedRoutesData.map(({ route, color, entries }) => (
          <g key={`checked-route-cov-${route.id}`} style={{ pointerEvents: "none" }}>
            {entries.map((entry) => {
              const loc = rects.find((r) => String(r.id) === String(entry.locationId));
              if (!loc || loc.is_root) return null;
              return (
                <rect
                  key={`cov-${route.id}-${entry.locationId}`}
                  x={loc.x}
                  y={loc.y}
                  width={loc.width}
                  height={loc.height}
                  fill={color}
                  opacity={0.18}
                />
              );
            })}
          </g>
        ))}

        {/* Bepipált route-ok - szintén rects UTÁN */}
        {checkedRoutesData.map(({ route, color, segments, entries }) => (
          <g key={`checked-route-${route.id}`} style={{ pointerEvents: "none" }}>
            {segments.map((segment) => (
              <polyline
                key={`cr-seg-${segment.key}`}
                points={segment.points.map(([x, y]) => `${x},${y}`).join(' ')}
                stroke={color}
                strokeWidth={8}
                strokeLinejoin="miter"
                strokeLinecap="square"
                fill="none"
                opacity={0.85}
              />
            ))}
            {entries.map((entry) => (
              <g key={`cr-marker-${route.id}-${entry.locationId}`}>
                <circle cx={entry.centerX} cy={entry.centerY} r={routeMarkerRadius} fill={color} opacity={0.85} stroke="#1e293b" strokeWidth={2} />
                <text x={entry.centerX} y={entry.centerY + 5} fontSize="11" fontWeight="bold" fill="#fff" textAnchor="middle" dominantBaseline="auto">
                  {route.code}{entry.stepOrder}
                </text>
              </g>
            ))}
          </g>
        ))}

        {/* Live View Route Plan overlay – ugyanazok a vizuális elemek mint a route tervezésnél */}
        {liveRoutePlans.map((plan) => {
          const rawEntries = (plan.steps || [])
            .map(step => {
              const loc = rects.find(r => String(r.id) === String(step.location_id));
              if (!loc || loc.is_root) return null;
              return { locationId: step.location_id, stepOrder: step.step_order, centerX: loc.x + loc.width / 2, centerY: loc.y + loc.height / 2 };
            })
            .filter(Boolean)
            .sort((a, b) => a.stepOrder - b.stepOrder);
          const { entries, segments } = computeAdaptiveRoute(rects, rawEntries);

          const sortedSteps = (plan.steps || []).slice().sort((a, b) => a.step_order - b.step_order);
          const stepReads = (step) => step.txn?.read_loctags ?? 0;
          // done: lezárt tranzakciók
          const doneSteps = sortedSteps.filter(s => s.txn && s.txn.status !== "in_progress");
          const inProgressStep = sortedSteps.find(s => s.txn?.status === "in_progress");
          // Takarító ikon csak ha már olvasott legalább 1 loctaget az adott helyen
          const activeStep = inProgressStep && stepReads(inProgressStep) > 0 ? inProgressStep : null;
          const currentStep = activeStep;
          // Számozandó helyek: még nem kész, és nincs aktív takarító ikon rajta
          const numberedSteps = sortedSteps.filter(s => {
            if (doneSteps.some(d => d.location_id === s.location_id)) return false;
            if (currentStep && s.location_id === currentStep.location_id) return false;
            return true;
          });

          const endTime = (() => {
            if (!plan.scheduled_time || !plan.duration_minutes) return null;
            const [h, m] = plan.scheduled_time.split(":").map(Number);
            const total = h * 60 + m + plan.duration_minutes;
            return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
          })();

          const clipId = `lrp-clip-${plan.plan_id}`;
          const photoR = routeMarkerRadius + 4;

          return (
            <g key={`lrp-${plan.plan_id}`} style={{ pointerEvents: "none" }}>
              {/* Szürke fill – ugyanaz mint a route tervezésnél: rgba(71,85,105,0.28) */}
              {doneSteps.map(step => {
                const loc = rects.find(r => String(r.id) === String(step.location_id));
                if (!loc || loc.is_root) return null;
                return <rect key={`lrp-fill-${plan.plan_id}-${step.location_id}`}
                  x={loc.x} y={loc.y} width={loc.width} height={loc.height}
                  fill="rgba(71,85,105,0.28)" />;
              })}

              {/* Route vonalak – ugyanolyan mint a tervezésnél: #9ca3af, strokeWidth 8 */}
              {segments.map(seg => (
                <polyline key={`lrp-seg-${plan.plan_id}-${seg.key}`}
                  points={seg.points.map(([x, y]) => `${x},${y}`).join(" ")}
                  stroke="#9ca3af" strokeWidth={8} fill="none"
                  strokeLinejoin="miter" strokeLinecap="square" opacity={0.95} />
              ))}

              {/* Elvégzett helyszínek: zöld ✓ vagy piros ! kör */}
              {doneSteps.map(step => {
                const entry = entries.find(e => String(e.locationId) === String(step.location_id));
                if (!entry) return null;
                const passed = step.txn?.status === "passed" || step.txn?.status === "cleaned";
                return (
                  <g key={`lrp-done-${plan.plan_id}-${step.location_id}`}>
                    <circle cx={entry.centerX} cy={entry.centerY} r={routeMarkerRadius}
                      fill={passed ? "#22c55e" : "#ef4444"} stroke="#4b5563" strokeWidth={2} />
                    <text x={entry.centerX} y={entry.centerY + 5} textAnchor="middle"
                      fontSize="14" fontWeight="bold" fill="#f9fafb"
                      style={{ pointerEvents: "none" }}>
                      {passed ? "✓" : "!"}
                    </text>
                  </g>
                );
              })}

              {/* Takarító aktuális pozíciója: körbe vágott fotó (vagy szürke kör ha nincs) */}
              {currentStep && (() => {
                const entry = entries.find(e => String(e.locationId) === String(currentStep.location_id));
                if (!entry) return null;
                return (
                  <g key={`lrp-worker-${plan.plan_id}`}>
                    <defs>
                      <clipPath id={clipId}>
                        <circle cx={entry.centerX} cy={entry.centerY} r={photoR} />
                      </clipPath>
                    </defs>
                    {plan.photo_url
                      ? <image href={plan.photo_url}
                          x={entry.centerX - photoR} y={entry.centerY - photoR}
                          width={photoR * 2} height={photoR * 2}
                          clipPath={`url(#${clipId})`}
                          preserveAspectRatio="xMidYMid slice" />
                      : <circle cx={entry.centerX} cy={entry.centerY} r={photoR}
                          fill="#9ca3af" stroke="#4b5563" strokeWidth={2} />
                    }
                    <circle cx={entry.centerX} cy={entry.centerY} r={photoR}
                      fill="none" stroke="#fff" strokeWidth={2.5} />
                  </g>
                );
              })()}

              {/* Következő helyszínek: szürke kör + 1, 2, 3… (hány van még előre) */}
              {numberedSteps.map((step, idx) => {
                const entry = entries.find(e => String(e.locationId) === String(step.location_id));
                if (!entry) return null;
                return (
                  <g key={`lrp-num-${plan.plan_id}-${step.location_id}`}>
                    <circle cx={entry.centerX} cy={entry.centerY} r={routeMarkerRadius}
                      fill="#9ca3af" stroke="#4b5563" strokeWidth={2} />
                    <text x={entry.centerX} y={entry.centerY + 5} textAnchor="middle"
                      fontSize="14" fontWeight="bold" fill="#f9fafb"
                      style={{ pointerEvents: "none" }}>
                      {idx + 1}
                    </text>
                  </g>
                );
              })}

              {/* Idő feliratok a route elején és végén */}
              {entries.length > 0 && plan.scheduled_time && (() => {
                const first = entries[0];
                const last = entries[entries.length - 1];
                return (
                  <g>
                    <text x={first.centerX} y={first.centerY - routeMarkerRadius - 5}
                      textAnchor="middle" fontSize="10" fontWeight="bold" fill="#94a3b8">
                      {plan.scheduled_time}
                    </text>
                    {last && endTime && entries.length > 1 && (
                      <text x={last.centerX} y={last.centerY - routeMarkerRadius - 5}
                        textAnchor="middle" fontSize="10" fontWeight="bold" fill="#94a3b8">
                        {endTime}
                      </text>
                    )}
                  </g>
                );
              })()}
            </g>
          );
        })}

        {draftRef.current && (
          <rect
            x={draftRef.current.x}
            y={draftRef.current.y}
            width={draftRef.current.width}
            height={draftRef.current.height}
            fill="none"
            stroke="#999"
            strokeDasharray="4"
            strokeWidth="1"
          />
        )}
        
        {/* Loctag-ek megjelenítése (csak ha editLoctagMode aktív) */}
        {editLoctagMode && loctags.map((tag) => {
          // Normál loctag: location + relative_position
          const location = rects.find(r => r.id === tag.location_id);
          if (!location) return null;
          
          const relPos = tag.relative_position || { x: 0, y: 0 };
          const tagX = location.x + relPos.x;
          const tagY = location.y + relPos.y;
          
          const isSelected = selectedLoctagId === tag.id;
          const isHovered = String(hoveredLoctagId) === String(tag.id);
          const tagColor = "#ef4444";
          
          return (
            <g key={tag.id}>
              {/* Nagyobb láthatatlan circle mobilon jobb touch target */}
              <circle
                cx={tagX}
                cy={tagY}
                r={12}
                fill="transparent"
                data-loctag-id={tag.id}
                className="loctag-circle"
                style={{ 
                  cursor: tag.isTemporary ? (draggingLoctag ? "grabbing" : "grab") : "pointer",
                  pointerEvents: 'auto',
                  touchAction: 'none'
                }}
                onMouseEnter={() => !rootSelectionOnly && setHoveredLoctagId && setHoveredLoctagId(tag.id)}
                onMouseLeave={(e) => {
                  // Ne zárjuk be ha a menü fölé megyünk
                  const relatedTarget = e.relatedTarget;
                  if (relatedTarget && (
                    relatedTarget.classList?.contains('loctag-menu') ||
                    relatedTarget.closest('.loctag-menu')
                  )) {
                    return;
                  }
                  setHoveredLoctagId && setHoveredLoctagId(null);
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  if (rootSelectionOnly) return;
                  // Csak ideiglenes (piros) tag drag-olható!
                  if (tag.isTemporary) {
                    setDraggingLoctag(tag);
                    setDragStartPos({ x: tagX, y: tagY });
                  }
                }}
                onMouseUp={(e) => {
                  e.stopPropagation();
                  setDraggingLoctag(null);
                  setDragStartPos(null);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (rootSelectionOnly) {
                    handleClick(e);
                    return;
                  }
                  if (!draggingLoctag) {
                    onLoctagClick && onLoctagClick(e);
                  }
                }}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (rootSelectionOnly) return;
                  // Csak ideiglenes (piros) tag drag-olható!
                  if (tag.isTemporary) {
                    touchStartTimeRef.current = Date.now();
                    touchStartPosRef.current = { x: tagX, y: tagY };
                    setTimeout(() => {
                      if (touchStartTimeRef.current && Date.now() - touchStartTimeRef.current >= 150) {
                        setDraggingLoctag(tag);
                        setDragStartPos({ x: tagX, y: tagY });
                      }
                    }, 150);
                  }
                }}
                onTouchMove={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (rootSelectionOnly) return;
                  
                  // Csak ideiglenes (piros) tag drag-olható!
                  if (tag.isTemporary && touchStartTimeRef.current) {
                    const duration = Date.now() - touchStartTimeRef.current;
                    
                    if (duration >= 150) {
                      if (!draggingLoctag) {
                        setDraggingLoctag(tag);
                      }
                      
                      if (svgRef.current && e.touches[0]) {
                        const touch = e.touches[0];
                        const pt = svgRef.current.createSVGPoint();
                        pt.x = touch.clientX;
                        pt.y = touch.clientY;
                        const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
                        const offsetX = canvasOffset?.x || 0;
                        const offsetY = canvasOffset?.y || 0;
                        const x = (svgP.x - offsetX) / canvasZoom;
                        const y = (svgP.y - offsetY) / canvasZoom;
                        
                        if (onLoctagDrag) {
                          onLoctagDrag(tag, x, y);
                        }
                      }
                    }
                  }
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (rootSelectionOnly) {
                    handleClick(e);
                    return;
                  }
                  // Csak ideiglenes (piros) tag drag-olható!
                  if (tag.isTemporary && touchStartTimeRef.current) {
                    const duration = Date.now() - touchStartTimeRef.current;
                    
                    // Ha gyors volt (< 200ms) és nem volt drag → TAP → toggle hover (delete menü)
                    if (duration < 200 && !draggingLoctag) {
                      if (hoveredLoctagId === tag.id) {
                        setHoveredLoctagId && setHoveredLoctagId(null);
                      } else {
                        setHoveredLoctagId && setHoveredLoctagId(tag.id);
                      }
                    }
                    
                    // Ha drag volt → mentés backend-re
                    if (draggingLoctag && e.changedTouches[0]) {
                      // Végső pozíció kiszámítása
                      const touch = e.changedTouches[0];
                      const pt = svgRef.current.createSVGPoint();
                      pt.x = touch.clientX;
                      pt.y = touch.clientY;
                      const svgP = pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
                      const offsetX = canvasOffset?.x || 0;
                      const offsetY = canvasOffset?.y || 0;
                      const finalX = (svgP.x - offsetX) / canvasZoom;
                      const finalY = (svgP.y - offsetY) / canvasZoom;
                      
                      if (onLoctagDragEnd) {
                        onLoctagDragEnd(tag, finalX, finalY);
                      }
                      setDraggingLoctag(null);
                      setDragStartPos(null);
                    }
                    
                    touchStartTimeRef.current = null;
                    touchStartPosRef.current = null;
                  }
                }}
              />

              {isHovered && (
                <g
                  className="loctag-hover-halo"
                  filter="url(#loctag-hover-glow)"
                  style={{ pointerEvents: "none" }}
                >
                  <circle
                    cx={tagX}
                    cy={tagY}
                    r={11}
                    fill="rgba(239, 68, 68, 0.42)"
                    stroke="#fecaca"
                    strokeWidth={2.5}
                  />
                  <circle
                    cx={tagX}
                    cy={tagY}
                    r={6.8}
                    fill="#ef4444"
                    stroke="#ef4444"
                    strokeWidth={2.2}
                  />
                </g>
              )}
              
              {/* Látható marker (normál: precíziós ⌖ jel, ideiglenes: piros kör) */}
              {tag.isTemporary ? (
                <circle
                  cx={tagX}
                  cy={tagY}
                  r={8}
                  fill="#ef4444"
                  stroke="#991b1b"
                  strokeWidth={3}
                  style={{ pointerEvents: "none" }}
                />
              ) : (
                <g
                  style={{ pointerEvents: "none" }}
                  opacity={isSelected || isHovered ? 1 : 0.76}
                >
                  {/* Outer ring */}
                  <circle
                    cx={tagX}
                    cy={tagY}
                    r={isSelected || isHovered ? 5.2 : 3.5}
                    fill={isHovered ? "rgba(255, 255, 255, 0.96)" : "rgba(255, 255, 255, 0.78)"}
                    stroke={tagColor}
                    strokeWidth={isSelected || isHovered ? 1.8 : 0.9}
                  />
                  {/* Crosshair lines */}
                  <line
                    x1={tagX - 5.6}
                    y1={tagY}
                    x2={tagX - 2.8}
                    y2={tagY}
                    stroke={tagColor}
                    strokeWidth={isHovered ? 1.5 : 0.9}
                    strokeLinecap="round"
                  />
                  <line
                    x1={tagX + 2.8}
                    y1={tagY}
                    x2={tagX + 5.6}
                    y2={tagY}
                    stroke={tagColor}
                    strokeWidth={isHovered ? 1.5 : 0.9}
                    strokeLinecap="round"
                  />
                  <line
                    x1={tagX}
                    y1={tagY - 5.6}
                    x2={tagX}
                    y2={tagY - 2.8}
                    stroke={tagColor}
                    strokeWidth={isHovered ? 1.5 : 0.9}
                    strokeLinecap="round"
                  />
                  <line
                    x1={tagX}
                    y1={tagY + 2.8}
                    x2={tagX}
                    y2={tagY + 5.6}
                    stroke={tagColor}
                    strokeWidth={isHovered ? 1.5 : 0.9}
                    strokeLinecap="round"
                  />
                  {/* Center dot */}
                  <circle
                    cx={tagX}
                    cy={tagY}
                    r={isSelected || isHovered ? 2.4 : 1.6}
                    fill={tagColor}
                    stroke={tagColor}
                    strokeWidth={0.5}
                  />
                </g>
              )}
              
            </g>
          );
        })}
        
        {/* Device and signed-in cleaner markers */}
        <defs>
          <filter id="device-glow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="2.2" result="blur"/>
            <feMerge>
              <feMergeNode in="blur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <style>{`
            @keyframes presence-pulse {
              0%, 100% { opacity: .75; transform: scale(1); }
              50% { opacity: .2; transform: scale(1.3); }
            }
            .presence-pulse { animation: presence-pulse 1.6s ease-in-out infinite; transform-box: fill-box; transform-origin: center; }
          `}</style>
        </defs>
        {cleanerPositions.map((pos, index) => {
          const isHovered = hoveredDeviceId === pos.deviceId;
          const hasUser = Boolean(pos.userName);
          const radius = isHovered ? 17 : 11;
          const clipId = `cleaner-avatar-${index}`;
          return (
            <g key={`cleaner-${pos.deviceId}`}
              ref={(el) => {
                if (el && !cleanerRefs.current.get(pos.deviceId)) {
                  cleanerRefs.current.set(pos.deviceId, el);
                  gsap.set(el, { x: pos.x, y: pos.y });
                }
              }}
              onMouseEnter={() => setHoveredDeviceId(pos.deviceId)}
              onMouseLeave={() => setHoveredDeviceId(null)}
              style={{ cursor: "pointer" }}
            >
              <circle className="presence-pulse" cx="0" cy="0" r={radius + 4}
                fill="none" stroke={hasUser ? "#14b8a6" : "#94a3b8"} strokeWidth="2"
                vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
              {hasUser ? (
                <g filter="url(#device-glow)">
                  <defs><clipPath id={clipId}><circle cx="0" cy="0" r={radius} /></clipPath></defs>
                  <circle cx="0" cy="0" r={radius} fill="#0f766e" stroke="white" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                  {pos.userPhotoUrl ? (
                    <image href={pos.userPhotoUrl} x={-radius} y={-radius} width={radius * 2} height={radius * 2}
                      preserveAspectRatio="xMidYMid slice" clipPath={`url(#${clipId})`} />
                  ) : (
                    <text x="0" y="4" textAnchor="middle" fill="white" fontSize={radius * .9} fontWeight="700">
                      {(pos.userName || "?").slice(0, 1).toUpperCase()}
                    </text>
                  )}
                  <circle cx="0" cy="0" r={radius} fill="none" stroke="white" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                </g>
              ) : (
                <g filter="url(#device-glow)">
                  <circle cx="0" cy="0" r={radius} fill="#64748b" stroke="white" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                  <path d="M-4 -7 L4 4 M1 3 L6 8 M4 2 L8 6 M-1 5 L3 9"
                    fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke" style={{ pointerEvents: "none" }} />
                </g>
              )}
              {isHovered && (
                <g transform={`translate(0 ${-(radius + 29)})`} style={{ pointerEvents: "none" }}>
                  <rect x="-64" y="-2" width="128" height={hasUser ? 30 : 19} rx="5" fill="rgba(15,23,42,.94)" stroke="#475569" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                  {hasUser && <text x="0" y="9" textAnchor="middle" fill="white" fontSize="9" fontWeight="700">{pos.userName}</text>}
                  <text x="0" y={hasUser ? 22 : 11} textAnchor="middle" fill={hasUser ? "#5eead4" : "white"} fontSize="8">{pos.deviceName || pos.deviceId}</text>
                </g>
              )}
            </g>
          );
        })}

        {/* Hover menü külön felső rétegen, hogy ne takarják ki más loctag-ek */}
        {editLoctagMode && (() => {
          const hoveredTag = loctags.find((tag) => tag.id === hoveredLoctagId);
          if (!hoveredTag || hoveredTag.isTemporary) return null;

          const location = rects.find((r) => r.id === hoveredTag.location_id);
          if (!location) return null;

          const relPos = hoveredTag.relative_position || { x: 0, y: 0 };
          const tagX = location.x + relPos.x;
          const tagY = location.y + relPos.y;

          return (
            <g>
              <foreignObject
                x={tagX + 8}
                y={tagY - 28}
                width="88"
                height="48"
                style={{ pointerEvents: "auto" }}
              >
                <div
                  className="loctag-menu flex flex-col gap-1 bg-white/90 border border-gray-300 rounded p-1 shadow"
                  style={{ pointerEvents: "auto" }}
                  onMouseEnter={() => setHoveredLoctagId && setHoveredLoctagId(hoveredTag.id)}
                  onMouseLeave={() => setHoveredLoctagId && setHoveredLoctagId(null)}
                >
                  <div className="text-[9px] text-gray-600 font-mono px-1">
                    #{hoveredTag.id}
                  </div>
                  <button
                    className="text-[9px] hover:bg-gray-100 px-1 rounded cursor-pointer"
                    style={{ pointerEvents: "auto" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onLoctagDelete && onLoctagDelete(hoveredTag.id);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    Delete
                  </button>
                </div>
              </foreignObject>
            </g>
          );
        })()}
      </g>

    </svg>
  );
};

export default LocationCanvas;
